import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail, getToken, setToken } from "@/lib/api";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  // status: "loading" | "authed" | "anon"
  const [status, setStatus] = useState("loading");
  const [admin, setAdmin] = useState(null);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setAdmin(null);
      setStatus("anon");
      return;
    }
    try {
      const { data } = await api.get("/admin/me");
      setAdmin(data);
      setStatus("authed");
    } catch {
      setToken(null);
      setAdmin(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/admin/login", { email, password });
      setToken(data.token);
      setAdmin(data.admin);
      setStatus("authed");
      return { ok: true };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/admin/logout");
    } catch {
      // ignore; clearing token client-side is sufficient
    }
    setToken(null);
    setAdmin(null);
    setStatus("anon");
  }, []);

  return (
    <AdminAuthContext.Provider value={{ status, admin, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
};

export default AdminAuthContext;
